import { Spacing, Typography } from '@persona-id/design-tokens';
import { ChevronRightIcon } from '@persona-id/design-tokens/icons';
import Fuse from 'fuse.js';
import { FC, Suspense, useMemo, useState } from 'react';
import { Control } from 'react-hook-form';
import { useRouteMatch } from 'react-router';
import { Link, Navigate, Route } from 'react-router-dom-v5-compat';
import { COLORS } from 'shared/styles/variables';
import styled from 'styled-components';

import Accordion, { TitleContainer } from 'dashboard/components/Accordion';
import LocalizedDirection from 'dashboard/components/LocalizedDirection';
import NavBack from 'dashboard/components/NavBack';
import { SentryRoutes } from 'dashboard/components/SentryRoute/SentryRoute';
import { fontImportUrl } from 'dashboard/components/ThemedFonts/ThemedFonts';
import { ThemeSetAttributes } from 'dashboard/models/theme-set';
import { Z_INDICES } from 'dashboard/styles/variables';
import {
  getAllAttributeControls,
  ThemeSetEditorFormCategory,
} from 'dashboard/views/DashboardThemeSetsEditorNewView/theme-set-editor-form-categories';
import ThemeSetEditorFormControl from 'dashboard/views/DashboardThemeSetsEditorNewView/ThemeSetEditorFormControl';
import ThemeSetEditorTemplatePreviewControl, {
  ThemeSetEditorTemplatePreviewControlProps,
} from 'dashboard/views/DashboardThemeSetsEditorNewView/ThemeSetEditorTemplatePreviewControl';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import Stack from 'design-system/components/Stack/Stack';
import { Label, Paragraph } from 'design-system/components/Text';
import useDebounced from 'lib/hooks/useDebounced';
import useExternalStylesheet from 'lib/hooks/useExternalStylesheet';
import { GoogleFonts } from 'lib/interfaces/fonts';
import { removeLastPath } from 'lib/utils/url';

const StyledIconChevron = styled(ChevronRightIcon)`
  rect[stroke],
  path[stroke] {
    stroke: ${COLORS.black};
  }
`;

const MenuItem = styled(Link)`
  cursor: pointer;

  ${Typography.TextMediumBold};
  color: ${COLORS.black};

  padding: ${Spacing[5]} ${Spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};

  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background-color: ${({ theme }) => theme.color.background.highlight};
  }
`;

const CategorySectionHeader = styled(HStack)`
  padding: ${Spacing[5]} ${Spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
  background: ${({ theme }) => theme.color.background.primary};
  position: sticky;
  top: 0;
  z-index: ${Z_INDICES.sticky};
`;

const Section = styled('div')`
  padding: ${Spacing[4]};
  padding-bottom: 0;
`;

const SubCategoryAccordion = styled(Accordion)`
  /* Don't give the SubCategoryAccordion a top border if it is the topmost item in the whole category */
  /* (This is determined by nth-child(2) since the CategorySectionHeader will always be the first child in the category) */
  &:not(:nth-child(2)) {
    border-top: 1px solid ${({ theme }) => theme.color.border.primary};
  }
  &:last-child {
    border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
  }

  ${TitleContainer} {
    padding: ${Spacing[4]};
  }

  &:hover > ${TitleContainer} {
    background-color: ${({ theme }) => theme.color.background.highlight};
  }
`;

interface ThemeSetEditorFormRoutesProps extends ThemeSetEditorCategoriesMenuProps {
  categoriesSchema: ThemeSetEditorFormCategory[];
  control: Control<ThemeSetAttributes, object>;
  disabled?: boolean;
  reactRouterV6?: boolean;
}

const ThemeSetEditorFormRoutes: FC<ThemeSetEditorFormRoutesProps> = ({
  categoriesSchema,
  control,
  disabled,
  reactRouterV6,
  ...themeSetEditorMenuProps
}) => {
  // This is done to load the Google fonts for the font previews for font family controls
  // NOTE: we are calling a hook on a loop on a constant value, so we'll never
  // break the rules of hooks.
  Object.values(GoogleFonts).map((fontFamily) => useExternalStylesheet(fontImportUrl(fontFamily))); // eslint-disable-line react-hooks/rules-of-hooks

  return (
    <SentryRoutes>
      <Route
        index
        element={
          <ThemeSetEditorCategoriesMenu
            categoriesSchema={categoriesSchema}
            control={control}
            disabled={disabled}
            {...themeSetEditorMenuProps}
          />
        }
      />
      {categoriesSchema.map((category) => (
        <Route
          key={category.path}
          path={category.path}
          element={
            <ThemeSetEditorFormCategorySection
              category={category}
              control={control}
              disabled={disabled}
              reactRouterV6={reactRouterV6}
            />
          }
        />
      ))}
      <Route path='*' element={<Navigate to='.' replace />} />
    </SentryRoutes>
  );
};

export default ThemeSetEditorFormRoutes;

interface ThemeSetEditorCategoriesMenuProps extends ThemeSetEditorTemplatePreviewControlProps {
  categoriesSchema: ThemeSetEditorFormCategory[];
  control: Control<ThemeSetAttributes, object>;
  disabled?: boolean;
}

const ThemeSetEditorCategoriesMenu: FC<ThemeSetEditorCategoriesMenuProps> = ({
  categoriesSchema,
  control,
  disabled,
  ...templatePreviewControlProps
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSetSearchTerm] = useDebounced(setSearchTerm, 300, {
    leading: false,
    trailing: true,
  });
  return (
    <div>
      <ThemeSetEditorTemplatePreviewControl
        {...templatePreviewControlProps}
        searchTerm={searchTerm}
        handleSetSearchTerm={debouncedSetSearchTerm}
      />
      <div>
        {searchTerm !== '' ? (
          <div>
            <ThemeSetEditorSearchResultSection
              searchTerm={searchTerm}
              control={control}
              disabled={disabled}
            />
          </div>
        ) : (
          categoriesSchema.map((category) => (
            <MenuItem key={category.path} to={category.path}>
              {category.heading}
              <StyledIconChevron />
            </MenuItem>
          ))
        )}
      </div>
    </div>
  );
};

interface ThemeSetEditorFormCategorySectionProps {
  category: ThemeSetEditorFormCategory;
  control: Control<ThemeSetAttributes, object>;
  disabled?: boolean;
  reactRouterV6?: boolean;
}

const ThemeSetEditorFormCategorySection: FC<ThemeSetEditorFormCategorySectionProps> = ({
  category,
  control,
  disabled,
  reactRouterV6,
}) => {
  const { heading, description, controls, subCategories } = category;

  const { url: matchUrl } = useRouteMatch();
  const rootUrl = removeLastPath(matchUrl);

  return (
    <Suspense fallback={null}>
      <LocalizedDirection>
        <CategorySectionHeader align='center'>
          <NavBack to={reactRouterV6 ? '..' : rootUrl} aria-label='Go back' />
          <Stack spacing={Spacing[0]}>
            <h3>{heading}</h3>
            {description && <Paragraph>{description}</Paragraph>}
          </Stack>
        </CategorySectionHeader>
        {controls.length > 0 && (
          <Section>
            {controls.map((controlConfig) => (
              <ThemeSetEditorFormControl
                key={controlConfig.name}
                controlConfig={controlConfig}
                control={control}
                disabled={disabled}
              />
            ))}
          </Section>
        )}
        {subCategories?.map((subCategory) => (
          <ThemeSetEditorFormSubCategory
            key={subCategory.heading}
            subCategory={subCategory}
            control={control}
            disabled={disabled}
          />
        ))}
      </LocalizedDirection>
    </Suspense>
  );
};

interface ThemeSetEditorFormSubCategoryProps {
  subCategory: Pick<ThemeSetEditorFormCategory, 'heading' | 'description' | 'controls'>;
  control: Control<ThemeSetAttributes, object>;
  disabled?: boolean;
}

const ThemeSetEditorFormSubCategory: FC<ThemeSetEditorFormSubCategoryProps> = ({
  subCategory,
  control,
  disabled,
}) => {
  const { heading, description, controls } = subCategory;
  return (
    <SubCategoryAccordion
      title={
        <div>
          {heading}
          {description && <Paragraph>{description}</Paragraph>}
        </div>
      }
      defaultExpanded={true}
    >
      <Section>
        {controls.map((controlConfig) => (
          <ThemeSetEditorFormControl
            key={controlConfig.name}
            controlConfig={controlConfig}
            control={control}
            disabled={disabled}
          />
        ))}
      </Section>
    </SubCategoryAccordion>
  );
};

interface ThemeSetEditorSearchResultSectionProps {
  searchTerm: string;
  control: Control<ThemeSetAttributes, object>;
  disabled?: boolean;
}

const ThemeSetEditorSearchResultSection: FC<ThemeSetEditorSearchResultSectionProps> = ({
  searchTerm,
  disabled,
  control,
}) => {
  const fuse = useMemo(() => {
    const fuseOptions = {
      shouldSort: true,
      findAllMatches: true,
      threshold: 0.2,
      ignoreLocation: true,
      minMatchCharLength: 1,
      keys: [
        { name: 'label', weight: 0.6 },
        { name: 'description', weight: 0.4 },
      ],
    };
    return new Fuse(getAllAttributeControls(), fuseOptions);
  }, []);

  const searchResult = fuse.search(searchTerm).map(({ item }) => item);
  return (
    <Suspense fallback={null}>
      <LocalizedDirection>
        {searchResult.length > 0 ? (
          <Section>
            <Label size='md' weight='bold'>
              Matching Attributes:
            </Label>
            <Spacer height={2} />
            {searchResult.map((controlConfig) => (
              <ThemeSetEditorFormControl
                key={controlConfig.name}
                controlConfig={controlConfig}
                control={control}
                disabled={disabled}
              />
            ))}
          </Section>
        ) : (
          <Section>
            <Paragraph weight='bold'>No matching attributes found</Paragraph>
          </Section>
        )}
      </LocalizedDirection>
    </Suspense>
  );
};
